import React, { useState, useEffect } from 'react';
import { quotaService } from '@/services/api';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Snackbar
} from '@mui/material';

interface UserQuota {
  id: string;
  user: {
    id: string;
    username: string;
  };
  job_quota: number;
}

const AdminUserQuotas: React.FC = () => {
  const [userQuotas, setUserQuotas] = useState<UserQuota[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingQuotas, setEditingQuotas] = useState<{ [key: string]: number }>({});

  useEffect(() => {
    console.log('Component mounted');
    const fetchUserQuotas = async () => {
      try {
        console.log('Fetching user quotas...');
        const response = await quotaService.getAllUserQuotas();
        console.log('Raw API response:', JSON.stringify(response, null, 2));
        
        // Ensure we have the correct data structure
        const quotas = Array.isArray(response) ? response : response.results || [];
        console.log('Processed quotas:', quotas);
        
        setUserQuotas(quotas);
        
        // Initialize editing state with current values
        const initialEditingState = quotas.reduce((acc: { [key: string]: number }, quota: UserQuota) => {
          if (!quota.id) {
            console.error('Quota missing ID:', quota);
            return acc;
          }
          console.log(`Initializing editing state for quota ${quota.id}:`, quota.job_quota);
          acc[quota.id] = quota.job_quota;
          return acc;
        }, {} as { [key: string]: number });
        
        console.log('Initial editing state:', initialEditingState);
        setEditingQuotas(initialEditingState);
      } catch (error) {
        console.error('Error fetching user quotas:', error);
        setError('Failed to fetch user quotas.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserQuotas();
  }, []);

  // Log state changes
  useEffect(() => {
    console.log('userQuotas updated:', userQuotas);
  }, [userQuotas]);

  useEffect(() => {
    console.log('editingQuotas updated:', editingQuotas);
  }, [editingQuotas]);

  const handleQuotaChange = (quotaId: string, newQuota: number) => {
    if (!quotaId) {
      console.error('Attempted to change quota with undefined ID');
      return;
    }
    
    console.log(`handleQuotaChange called for quota ${quotaId} with value ${newQuota}`);
    console.log('Current editingQuotas:', editingQuotas);
    
    setEditingQuotas(prev => {
      const newState = {
        ...prev,
        [quotaId]: newQuota
      };
      console.log('New editingQuotas state:', newState);
      return newState;
    });
  };

  const handleSaveQuota = async (quotaId: string) => {
    if (!quotaId) {
      console.error('Attempted to save quota with undefined ID');
      return;
    }
    
    console.log(`handleSaveQuota called for quota ${quotaId}`);
    console.log('Current editingQuotas:', editingQuotas);
    
    try {
      const newQuota = editingQuotas[quotaId];
      console.log(`Saving new quota value ${newQuota} for quota ${quotaId}`);
      
      await quotaService.updateUserQuota(quotaId, newQuota);
      console.log('API call successful');
      
      // Update the main state only after successful API call
      setUserQuotas(prevQuotas => {
        const newQuotas = prevQuotas.map(quota =>
          quota.id === quotaId ? { ...quota, job_quota: newQuota } : quota
        );
        console.log('Updated userQuotas:', newQuotas);
        return newQuotas;
      });
      
      setSuccessMessage('Quota updated successfully.');
    } catch (error) {
      console.error('Error updating user quota:', error);
      setError('Failed to update user quota.');
      
      // Revert the editing state to the original value
      const originalQuota = userQuotas.find(q => q.id === quotaId)?.job_quota || 0;
      console.log(`Reverting quota ${quotaId} to original value:`, originalQuota);
      
      setEditingQuotas(prev => {
        const newState = {
          ...prev,
          [quotaId]: originalQuota
        };
        console.log('Reverted editingQuotas:', newState);
        return newState;
      });
    }
  };

  return (
    <Container maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom>
        Manage User Quotas
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Job Quota</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userQuotas.map(quota => {
                if (!quota.id) {
                  console.error('Rendering quota without ID:', quota);
                  return null;
                }
                
                console.log(`Rendering quota ${quota.id}:`, {
                  currentValue: editingQuotas[quota.id] ?? quota.job_quota,
                  originalValue: quota.job_quota
                });
                
                return (
                  <TableRow key={quota.id}>
                    <TableCell>{quota.user.username}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={editingQuotas[quota.id] ?? quota.job_quota}
                        onChange={(e) => {
                          console.log(`TextField onChange for quota ${quota.id}:`, e.target.value);
                          const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                          if (!isNaN(value)) {
                            handleQuotaChange(quota.id, value);
                          }
                        }}
                        variant="outlined"
                        size="small"
                        inputProps={{ min: 0 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSaveQuota(quota.id)}
                      >
                        Save
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {error && (
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          message={error}
        />
      )}
      {successMessage && (
        <Snackbar
          open={Boolean(successMessage)}
          autoHideDuration={6000}
          onClose={() => setSuccessMessage(null)}
          message={successMessage}
        />
      )}
    </Container>
  );
};

export default AdminUserQuotas; 